import { CommonModule } from "@angular/common";
import { Component, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { DigitalAssetScreenSize, HtmlTemplateParameters, TemplateContentType } from "clearline-api";
import { BehaviorSubject, of } from "rxjs";
import { take } from "rxjs/operators";
import {
  CfsTemplateBaseComponent,
  CfsTemplateError,
  CfsTemplateErrorData,
  CfsTemplateMediaConfig,
  CFSTemplatePreviewType,
  CustomContentTemplateType,
  Template,
  TemplateAnimationType,
  TemplateConfigurationOptionsDto,
  TemplatePreviewOptions,
  TemplateProperties,
  UrlContentSettings
} from "../../../models";
import { defaultCfsTemplateMediaConfig } from "../cfs-template.config";
import { getDigitalAssetScreenSize, getFullDisplayTimeByTemplate, getIsDigitalScreenSizeResponsive } from "../cfs-template.utils";
import { GeneratedHtmlTemplateComponent } from "../generated-html-template";
import { HtmlTemplateComponent } from "../html-template";
import { CmcQrCodeTemplateComponent } from "../html-template/cmc-qr-code-template/cmc-qr-code-template.component";
import { ImageTemplateComponent } from "../image-template";
import { LottieTemplateComponent } from "../lottie-template";
import { TemplatePreviewService } from "../template-preview.service";
import { VideoTemplateComponent } from "../video-template";
import { CfsTemplateErrorTypes, generateCfsHtmlTemplateErrorMessage, generateCfsTemplateError } from "../cfs-template-errors";

@Component({
  selector: "lib-cfs-template-preview",
  templateUrl: "./cfs-template-preview.component.html",
  styleUrls: ["./cfs-template-preview.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    LottieTemplateComponent,
    HtmlTemplateComponent,
    VideoTemplateComponent,
    GeneratedHtmlTemplateComponent,
    ImageTemplateComponent,
    CmcQrCodeTemplateComponent
  ]
})
export class CfsTemplatePreviewComponent implements OnInit, CfsTemplateBaseComponent {
  @Input() animationType: TemplateAnimationType = TemplateAnimationType.Animated;
  @Input() isPrintView = false;
  @Input() responsive = false;
  @Input() isLowResolutionScreen = false;
  @Input() fullMediaConfig: CfsTemplateMediaConfig = defaultCfsTemplateMediaConfig;

  @Input() set screenSize(screenSize: DigitalAssetScreenSize) {
    this.setScreenSize(screenSize);
  }

  @Input() set template(template: Template | null) {
    if (template) {
      const { generatedBody, templateProperties, configuration } = template;
      const parameters: HtmlTemplateParameters | null = configuration?.parameters || null;
      const configurationOptions: TemplateConfigurationOptionsDto | null = configuration?.options || null;

      this.templateProperties = templateProperties || null; // uses for video and image templates
      this.generatedBody = generatedBody ? this.sanitizer.bypassSecurityTrustHtml(generatedBody as string) : "";
      this.displayTime = getFullDisplayTimeByTemplate(template);
      this.templatePreviewType = this.getTemplatePreviewTypeByTemplate(template);
      this.templateName = template.name || "";

      this.templatePreviewType$.next(this.templatePreviewType);

      if (this.templatePreviewType === CFSTemplatePreviewType.Lottie) {
        const previewOptions$ = template?.previewOptions
          ? of(template.previewOptions)
          : this.templatePreviewService.getLottieTemplatePreviewOptions(template);

        previewOptions$.pipe(take(1)).subscribe((previewOptions: TemplatePreviewOptions | null) => {
          const { digitalAsset } = previewOptions || {};
          this.isScreenSizeResponsive = getIsDigitalScreenSizeResponsive(digitalAsset);

          this.setPreviewOptions(previewOptions);

          if (this.responsive) {
            this.setScreenSize();
          }
        });
      } else if (parameters) {
        this.configurationOptions$.next(configurationOptions);
        this.parameters$.next(parameters);
      }
    }
  }

  @Input() set options(options: TemplatePreviewOptions | null) {
    if (options) {
      const {
        generatedBody,
        parameters = null,
        templateContentType = null,
        customContentTemplateType = null,
        templateProperties,
        configurationOptions = null
      } = options;
      this.templateProperties = templateProperties || null;
      this.templatePreviewType = this.getTemplatePreviewType(templateContentType, customContentTemplateType, templateProperties);

      this.templatePreviewType$.next(this.templatePreviewType);
      this.setPreviewOptions(options);
      this.parameters$.next(parameters);
      this.configurationOptions$.next(configurationOptions);

      if (generatedBody) {
        this.generatedBody = this.sanitizer.bypassSecurityTrustHtml(generatedBody);
      }
    }
  }

  @Output() printReady = new EventEmitter();
  @Output() ready = new EventEmitter();
  @Output() error = new EventEmitter<CfsTemplateError>();

  @ViewChild("currentTemplate") currentTemplateComponent?: CfsTemplateBaseComponent;

  readonly CFSTemplateType = CFSTemplatePreviewType;

  templatePreviewType: CFSTemplatePreviewType | null = null; // todo: try to merge with templateScreenSize$
  templateProperties: TemplateProperties | null = null;
  displayTime = 0;
  generatedBody: SafeHtml = "";
  templateName = "";

  previewOptions$ = new BehaviorSubject<TemplatePreviewOptions | null>(null);
  parameters$ = new BehaviorSubject<HtmlTemplateParameters | null>(null);
  configurationOptions$ = new BehaviorSubject<TemplateConfigurationOptionsDto | null>(null);
  templateScreenSize$ = new BehaviorSubject<DigitalAssetScreenSize | null>(null);
  templatePreviewType$ = new BehaviorSubject<CFSTemplatePreviewType | null>(null);

  private templateScreenSize: DigitalAssetScreenSize = DigitalAssetScreenSize.Full; // todo: merge with templateScreenSize$
  private isScreenSizeResponsive = false;
  private isTemplateReady = false;
  private isTemplateWithError = false;

  get isReady(): boolean {
    return this.isTemplateReady;
  }

  get hasError(): boolean {
    return this.isTemplateWithError;
  }

  @HostListener("window:resize") handleResizeEvent(): void {
    if (this.responsive) {
      this.setScreenSize();
    }
  }

  constructor(private sanitizer: DomSanitizer, private templatePreviewService: TemplatePreviewService) {}

  ngOnInit(): void {
    if (this.isLowResolutionScreen && this.isNonResponsiveTemplate()) {
      this.responsive = false;
    }
  }

  play(): void {
    if (this.currentTemplateComponent) {
      this.currentTemplateComponent.play();
    }
  }

  stop(): void {
    if (this.currentTemplateComponent) {
      this.currentTemplateComponent.stop();
    }
  }

  pause(): void {
    if (this.currentTemplateComponent) {
      this.currentTemplateComponent.pause();
    }
  }

  restart(): void {
    if (this.currentTemplateComponent) {
      this.currentTemplateComponent.restart();
    }
  }

  protected onTemplateReady(): void {
    this.setTemplateReady();
  }

  protected onTemplateError(error: CfsTemplateError): void {
    this.isTemplateWithError = true;
    this.error.emit(error);
  }

  protected onPrintReady(): void {
    this.setPrintReady();
  }

  private setTemplateReady(): void {
    this.isTemplateReady = true;
    this.ready.emit();
  }

  private setPrintReady(): void {
    if (this.isPrintView) {
      this.printReady.emit();
    }
  }

  private getTemplatePreviewTypeByTemplate(template: Template): CFSTemplatePreviewType {
    const { templateContentType = null, templateProperties } = template;
    const { ContentType = null } = (templateProperties as UrlContentSettings) || {};

    return this.getTemplatePreviewType(templateContentType, ContentType, templateProperties);
  }

  private getTemplatePreviewType(
    templateContentType: TemplateContentType | null,
    customContentType: CustomContentTemplateType | null,
    templateProperties: TemplateProperties | null = null
  ): CFSTemplatePreviewType {
    switch (templateContentType) {
      case TemplateContentType.EditableLottie:
        return CFSTemplatePreviewType.Lottie;
      case TemplateContentType.QrCodeAdvanced:
        return CFSTemplatePreviewType.Html;
      case TemplateContentType.QrCodeCmc:
        return CFSTemplatePreviewType.CmcQrCode;
      case TemplateContentType.CustomContent:
        const { ImageUrl, VideoUrl } = (templateProperties as UrlContentSettings) || {};

        if (customContentType === CustomContentTemplateType.Video) {
          return VideoUrl ? CFSTemplatePreviewType.Video : CFSTemplatePreviewType.GeneratedHtml;
        } else {
          return ImageUrl ? CFSTemplatePreviewType.Image : CFSTemplatePreviewType.GeneratedHtml;
        }
      default:
        return CFSTemplatePreviewType.GeneratedHtml;
    }
  }

  private setScreenSize(screenSize: DigitalAssetScreenSize | null = null): void {
    const updatedScreenSize: DigitalAssetScreenSize = screenSize ?? this.getCurrentScreenSizeType();

    if (updatedScreenSize && this.templateScreenSize !== updatedScreenSize) {
      this.templateScreenSize = updatedScreenSize ?? DigitalAssetScreenSize.Full;

      this.templateScreenSize$.next(this.templateScreenSize);
    }
  }

  private getCurrentScreenSizeType(): DigitalAssetScreenSize {
    return this.isScreenSizeResponsive ? getDigitalAssetScreenSize(this.isLowResolutionScreen) : DigitalAssetScreenSize.Full;
  }

  private isNonResponsiveTemplate(): boolean {
    const ignoredHtmlTemplateTypes = [CFSTemplatePreviewType.Html, CFSTemplatePreviewType.CmcQrCode];

    return !!this.templatePreviewType && ignoredHtmlTemplateTypes.includes(this.templatePreviewType);
  }

  private setPreviewOptions(options: TemplatePreviewOptions | null): void {
    if (options) {
      this.previewOptions$.next(options);
    } else {
      this.generateAndSetError(CfsTemplateErrorTypes.EmptyData);
    }
  }

  private generateAndSetError(errorType: CfsTemplateErrorTypes, dataField = "", dataValue = ""): void {
    const errorData: CfsTemplateErrorData | null = dataField ? { field: dataField, value: dataValue } : null;
    const message: string = generateCfsHtmlTemplateErrorMessage(errorType);
    const templateError: CfsTemplateError = generateCfsTemplateError(errorType, message, errorData);

    this.onTemplateError(templateError);
  }
}
